import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/TodasSolicitudes.css";
import SolicitudesPendientes from "./SolicitudesPendientes";
import DocumentViewer from "./DocumentViewer";
import useObtenerSolicitudes from "../hooks/useObtenerSolicitudes";
import useDropdownData from "../hooks/useDropdownData";
import {
  formatearHora,
  formatearFechaPlano,
  urlApi,
} from "../helpers/functionHelpers";
import Spinner from "./Spinner";

const TodasSolicitudes = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [expandedFolio, setExpandedFolio] = useState(null);
  const [searchTerm, setSearchTerm] = useState({
    folio: "",
    oc: "",
    insumo: "",
  });
  const [filteredSolicitudes, setFilteredSolicitudes] = useState(solicitudes);

  const {
    obtenerSolicitudes: obtenerSolicitudesExternas,
    loading: loadingExternas,
    error: errorExternas,
  } = useObtenerSolicitudes(`${urlApi}/api/solicitudes-agrupadas`);
  const {
    obtenerSolicitudes: obtenerSolicitudesInternas,
    loading: loadingInternas,
  } = useObtenerSolicitudes(`${urlApi}/api/solicitudesInternas`);
  const {
    obtenerSolicitudes: obtenerSolicitudesPasadas,
    loading: loadingPasadas,
  } = useObtenerSolicitudes(`${urlApi}/api/solicitudes-pasadas-agrupadas`);
  const {
    obtenerSolicitudes: obtenerSolicitudesArchivadas,
    loading: loadingArchivadas,
  } = useObtenerSolicitudes(`${urlApi}/api/solicitudes-archivadas-agrupadas`);
  const { data: statuses, loading: loadingStatuses } = useDropdownData(
    `${urlApi}/api/statuses`
  );

  const fetchSolicitudes = async () => {
    const data = await obtenerSolicitudesExternas();
    if (data) {
      const ordenadas = [...data].sort(
        (a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv)
      );
      setSolicitudes(ordenadas);
    }
  };

  const fetchSolicitudesInternas = async () => {
    const data = await obtenerSolicitudesInternas();
    if (data) {
      const ordenadas = [...data].sort(
        (a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv)
      );
      setSolicitudes(ordenadas);
    }
  };
  const fetchSolicitudesPasadas = async () => {
    const data = await obtenerSolicitudesPasadas();
    if (data) {
      const ordenadas = [...data].sort(
        (a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv)
      );
      setSolicitudes(ordenadas);
    }
  };
  const fetchSolicitudesArchivadas = async () => {
    const data = await obtenerSolicitudesArchivadas();
    if (data) {
      const ordenadas = [...data].sort(
        (a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv)
      );
      setSolicitudes(ordenadas);
    }
  };

  useEffect(() => {
    const { folio, oc, insumo } = searchTerm;

    const filtered = solicitudes.filter((solicitud) => {
      const matchesId = folio ? solicitud.folio.includes(folio) : true;
      const matchesOc = oc ? solicitud.numeroOC.includes(oc) : true;
      const matchesInsumo = insumo ? solicitud.nmInsumo.includes(insumo) : true;

      return matchesId && matchesOc && matchesInsumo;
    });

    setFilteredSolicitudes(filtered);
  }, [searchTerm, solicitudes]);

  // Manejo de cambios en los campos de búsqueda
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchTerm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectSolicitud = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
  };

  const handleCargarExternas = () => {
    fetchSolicitudes();
  };

  const handleCargarInternas = () => {
    fetchSolicitudesInternas();
  };

  const handleCargarPasadas = () => {
    fetchSolicitudesPasadas();
  };

  const handleCargarArchivadas = () => {
    fetchSolicitudesArchivadas();
  };

  const handleActualizar = () => {
    fetchSolicitudes();
  };

  if (loadingExternas || loadingInternas || loadingPasadas || loadingArchivadas || loadingStatuses) {
    return (
      <div className="loader-container">
        <div className="spinner-wrapper">
          <Spinner />
          <p className="loading-text">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lista-reservas-container">
      <h2>Todas las solicitudes</h2>
      <div className="toolbar">
        <motion.button
          className="toolbar-button"
          type="button"
          onClick={handleCargarExternas}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
        >
          Externas
        </motion.button>
        <motion.button
          className="toolbar-button"
          type="button"
          onClick={handleCargarInternas}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
        >
          Internas
        </motion.button>
        <motion.button
          className="toolbar-button"
          type="button"
          onClick={handleCargarPasadas}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
        >
          Pasadas
        </motion.button>
        <motion.button
          className="toolbar-button"
          type="button"
          onClick={handleCargarArchivadas}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
        >
          📦 Archivadas
        </motion.button>
        <motion.button
          className="toolbar-button"
          type="button"
          onClick={handleActualizar}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
        >
          Actualizar
        </motion.button>
        <input
          type="text"
          placeholder="Buscar por folio"
          className="search-input"
          name="folio"
          value={searchTerm.folio}
          onChange={handleInputChange}
        />
      </div>
      <table className="reservas-table">
        {loadingExternas && <p>Cargando solicitudes...</p>}
        {errorExternas && <p>Error: {errorExternas}</p>}
        <thead>
          <tr>
            <th>FOLIO</th>
            <th>PROVEEDOR</th>
            <th>OCs</th>
            <th>Insumos</th>
            <th>Status</th>
            <th>Llegada</th>
            <th>Fecha</th>
            <th>Horario</th>
            <th>Documentos</th>
          </tr>
        </thead>
        <tbody>
          {filteredSolicitudes.map((solicitud) => {
            const statusDescripcion =
              statuses.find((status) => status.id === solicitud.statuses_id)
                ?.description || "Sin descripción";
            
            // Determinar badge color para Estado de Llegada
            let estadoColor = "#9e9e9e"; // default gris
            if (solicitud.estadoLlegada === "A Tiempo") estadoColor = "#4caf50";
            else if (solicitud.estadoLlegada === "Fuera de Tiempo") estadoColor = "#ff9800";
            else if (solicitud.estadoLlegada === "Sin Cita") estadoColor = "#f44336";
            else if (solicitud.estadoLlegada === "Rechazado") estadoColor = "#d32f2f";

            return (
              <React.Fragment key={solicitud.folio}>
                <tr
                  onClick={() => handleSelectSolicitud(solicitud)}
                  className={"clickable-row"}
                >
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="folio-link full-width-link"
                    >
                      {solicitud.folio}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                    >
                      {solicitud.nombrePrv}
                    </Link>
                  </td>
                  <td>
                    <span className="badge-ocs" style={{ display: "inline-block", background: "#2196F3", color: "white", padding: "4px 12px", borderRadius: "20px", fontWeight: "bold", fontSize: "12px" }}>
                      {solicitud.total_ocs} OC{solicitud.total_ocs > 1 ? 's' : ''}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                      title={solicitud.insumos}
                    >
                      {solicitud.insumos && solicitud.insumos.length > 50 ? `${solicitud.insumos.substring(0, 50)}...` : solicitud.insumos}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                    >
                      {statusDescripcion}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                    >
                      {solicitud.estadoLlegada ? (
                        <span style={{ display: "inline-block", background: estadoColor, color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold" }}>
                          {solicitud.estadoLlegada}
                        </span>
                      ) : (
                        <span style={{ color: "#aaa", fontSize: "12px" }}>Por llegar</span>
                      )}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                    >
                      {formatearFechaPlano(solicitud.fechaRsv)}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/admin/pendientes/${solicitud.folio}`}
                      className="full-width-link"
                    >
                      {solicitud.dataIni && solicitud.dataFin ? `${formatearHora(solicitud.dataIni)} - ${formatearHora(solicitud.dataFin)}` : '-'}
                    </Link>
                  </td>
                  <td>
                    <button 
                      className="expand-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedFolio(expandedFolio === solicitud.folio ? null : solicitud.folio);
                      }}
                    >
                      {expandedFolio === solicitud.folio ? 'Ocultar' : 'Ver'}
                    </button>
                  </td>
                </tr>
                {expandedFolio === solicitud.folio && (
                  <tr>
                    <td colSpan="8" className="document-cell">
                      <DocumentViewer folio={solicitud.folio} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {solicitudSeleccionada && (
        <SolicitudesPendientes
          proveedor={solicitudSeleccionada.proveedor}
          transportista={solicitudSeleccionada.transportista}
        />
      )}
    </div>
  );
};

export default TodasSolicitudes;
